import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Particles from 'react-particles';
import { loadSlim } from 'tsparticles-slim';
import { supabase } from '../lib/supabase';
import documentTypes from '../data/documentTypes';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
      
      // Determine active section for animations
      const heroSection = document.getElementById('hero');
      const documentsSection = document.getElementById('documents');
      const featuresSection = document.getElementById('features');
      
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      if (documentsSection && scrollPosition >= documentsSection.offsetTop && 
          (!featuresSection || scrollPosition < featuresSection.offsetTop)) {
        setActiveSection('documents');
      } else if (featuresSection && scrollPosition >= featuresSection.offsetTop) {
        setActiveSection('features');
      } else {
        setActiveSection('hero');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('document_types')
          .select('*')
          .order('title')
          .limit(4); // Getting just 4 for the homepage
          
        if (error) throw error;
        
        setDocumentTypes(data || []);
      } catch (error) {
        console.error('Error fetching document types:', error);
        // Fallback to empty array if there's an error
        setDocumentTypes([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDocumentTypes();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col font-['Poppins',sans-serif] bg-gray-50">
      {/* Hero Section */}
      <div id="hero" className="relative min-h-screen flex items-center bg-gradient-to-br from-blue-600 to-indigo-900">
        {/* Particle Background */}
        <Particles
          id="tsparticles"
          init={particlesInit}
          className="absolute inset-0"
          options={{
            background: {
              color: {
                value: "transparent",
              },
            },
            particles: {
              color: {
                value: "#ffffff",
              },
              number: {
                value: 50,
                density: {
                  enable: true,
                  value_area: 800,
                },
              },
              opacity: {
                value: 0.3,
                random: true,
              },
              size: {
                value: 3,
                random: true,
              },
              move: {
                enable: true,
                speed: 1,
                random: true,
                outModes: "out",
              },
              links: {
                enable: true,
                distance: 150,
                color: "#ffffff",
                opacity: 0.2,
                width: 1,
              },
            },
            interactivity: {
              events: {
                onHover: {
                  enable: true,
                  mode: "grab",
                },
                onClick: {
                  enable: true, 
                  mode: "push",
                },
              },
              modes: {
                grab: {
                  distance: 140,
                  links: {
                    opacity: 0.5,
                  },
                },
              },
            },
          }}
        />
        
        <div className="container mx-auto px-4 py-10 z-10 text-center relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <motion.h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              <motion.span 
                className="block"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                Legal Documents
              </motion.span>
              <motion.span 
                className="block mt-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
              >
                Made Simple
              </motion.span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto font-light"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              Generate professional legal documents in minutes with our AI-powered platform.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block"
            >
              <Link 
                to="#documents" 
                className="px-8 py-4 text-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full font-medium hover:shadow-lg hover:shadow-indigo-500/50 transition-all relative overflow-hidden group"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('documents');
                }}
              >
                <span className="relative z-10">Get Started</span>
                <motion.span 
                  className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 -z-10"
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.5, 1] }}
                  transition={{ duration: 2, times: [0, 0.5, 1], repeat: Infinity, repeatDelay: 5 }}
                  style={{ borderRadius: '9999px' }}
                ></motion.span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Documents Section */}
      <motion.div 
        id="documents" 
        className="container mx-auto px-4 py-24"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-4xl font-bold text-center mb-4">Choose Your Document</h2>
        <p className="text-gray-600 text-center mb-16 max-w-2xl mx-auto">
          Select from our range of legal document templates to get started. Our AI will guide you through the process.
        </p>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {documentTypes.map((document, index) => (
              <motion.div 
                key={document.id}
                className={`${index % 2 !== 0 ? 'md:translate-y-10' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                  delay: 0.1 + (index * 0.05)
                }}
              >
                <Link 
                  to={`/form/${document.id}`} 
                  className="block h-full bg-white/90 backdrop-blur-md rounded-xl shadow-md border border-gray-200/20 hover:shadow-xl hover:shadow-blue-500/20 hover:translate-y-[-8px] transition-all duration-200 ease-out overflow-hidden relative group"
                >
                  <div className="h-32 bg-gradient-to-r from-primary to-blue-700 flex items-center justify-center relative">
                    <div className="text-white text-4xl relative z-10">
                      {document.id === "freelance-agreement" && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                          <path d="M2 2l7.586 7.586"></path>
                          <circle cx="11" cy="11" r="2"></circle>
                        </svg>
                      )}
                      {document.id === "nda" && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      )}
                      {document.id === "terms-and-conditions" && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      )}
                      {document.id === "employment-contract" && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="8.5" cy="7" r="4"></circle>
                          <polyline points="17 11 19 13 23 9"></polyline>
                        </svg>
                      )}
                      {document.id === "cease-and-desist" && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                        </svg>
                      )}
                    </div>
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-indigo-600/40"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{document.title}</h3>
                    <p className="text-gray-600">{document.description}</p>
                    
                    {/* Secondary action that appears on hover */}
                    <div className="absolute bottom-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm opacity-0 transform translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 ease-out">
                      Create
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* View All Documents Button */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <Link 
            to="/templates" 
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full text-lg font-medium hover:shadow-lg hover:shadow-indigo-500/30 transition-all hover:scale-105 group"
            onClick={() => {
              // Ensure we scroll to top when navigating
              window.scrollTo(0, 0);
              // Clear any previous template page state to avoid flickering
              sessionStorage.removeItem('templatesPageState');
            }}
          >
            <span>View All Documents</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </motion.div>
      </motion.div>

      {/* Features Section */}
      <motion.div 
        id="features" 
        className="mt-16 max-w-6xl mx-auto mb-24"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-4xl font-bold text-center mb-16">Why Choose LegalGenius</h2>
        
        <div className="grid md:grid-cols-3 gap-12">
          <motion.div 
            className="text-center relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div 
              className="bg-gradient-to-br from-blue-200 to-indigo-300 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 relative z-10"
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
              </svg>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
            <p className="text-gray-600">Generate documents in minutes, not days</p>
          </motion.div>
          
          <motion.div 
            className="text-center relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div 
              className="bg-gradient-to-br from-blue-200 to-indigo-300 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 relative z-10"
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.4 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2">Legally Sound</h3>
            <p className="text-gray-600">Documents crafted with legal expertise</p>
          </motion.div>
          
          <motion.div 
            className="text-center relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div 
              className="bg-gradient-to-br from-blue-200 to-indigo-300 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 relative z-10"
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2">Customizable</h3>
            <p className="text-gray-600">Tailor documents to your specific needs</p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default HomePage;